/**
 * Simulation History section with two views:
 * - "By Simulation": flat chronological list of attempts (default, preserves deep-link highlight).
 * - "By Module": attempts grouped per skill (reading/listening/writing/speaking), chronological within each.
 *
 * Both views support a pass/fail status filter.
 *
 * @module SimulationHistorySection
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type CSSProperties,
} from "react";
import {
  AlertTriangle,
  Award,
  Calendar,
  ChevronDown,
  Layers,
  List,
  Loader2,
  RotateCcw,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  examTypeLabels,
  type SimulationResult,
} from "@/types/simulation-results";
import { cn } from "@/lib/utils";
import {
  SIMULATION_MODULE_COLORS,
  SIMULATION_MODULE_KEYS,
  type SimulationModuleKey,
} from "@/lib/simulationResultsChartData";
import {
  SIMULATION_MODULE_ICONS,
  SIMULATION_MODULE_SHORT_LABELS,
} from "@/lib/simulationModuleDisplay";
import { SimulationResultCard } from "@/components/results/SimulationResultCard";
import {
  getModuleAnalysisDisplayInfo,
  isModuleReviewAvailable,
} from "@/lib/analysisStatus";

export type HistoryView = "simulation" | "module";
export type StatusFilter = "all" | "passed" | "failed";

const MODULE_ORDER: readonly SimulationModuleKey[] = [
  "reading",
  "listening",
  "writing",
  "speaking",
];

/** URL search param storing comma-separated folded module keys (module view only). */
export const FOLDED_MODULES_SEARCH_PARAM = "folded";

export function parseFoldedModules(
  raw: string | null,
): ReadonlySet<SimulationModuleKey> {
  if (!raw) {
    return new Set();
  }

  const folded = raw
    .split(",")
    .map((value) => value.trim())
    .filter((value): value is SimulationModuleKey =>
      SIMULATION_MODULE_KEYS.includes(value as SimulationModuleKey),
    );

  return new Set(folded);
}

export function serializeFoldedModules(
  folded: ReadonlySet<SimulationModuleKey>,
): string | null {
  if (folded.size === 0) {
    return null;
  }

  return MODULE_ORDER.filter((moduleKey) => folded.has(moduleKey)).join(",");
}

function attemptRefKey(sessionId: string, moduleName: string) {
  return `${sessionId}-${moduleName}`;
}

interface ModuleAttempt {
  readonly result: SimulationResult;
  readonly module: SimulationResult["modules"][number];
}

export interface SimulationHistorySectionProps {
  readonly results: SimulationResult[];
  readonly highlightedSessionId: string | null;
  /** Module name from `?module=` for deep-link highlight in By Module view. */
  readonly highlightedModuleName: string | null;
  readonly onModuleClick: (resultId: string, moduleName: string) => void;
  readonly onModuleRetry?: (
    resultId: string,
    moduleName: string,
  ) => void | Promise<void>;
  readonly view: HistoryView;
  readonly statusFilter: StatusFilter;
  readonly onViewChange: (view: HistoryView) => void;
  readonly onStatusFilterChange: (statusFilter: StatusFilter) => void;
  /** Folded module section keys from `?folded=` (module view only). */
  readonly foldedModules: ReadonlySet<SimulationModuleKey>;
  readonly onFoldedModulesChange: (
    folded: ReadonlySet<SimulationModuleKey>,
  ) => void;
}

function formatAttemptDate(date: string) {
  return new Date(date).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ModuleAttemptRow({
  attempt,
  onClick,
  onRetry,
  highlighted = false,
}: {
  readonly attempt: ModuleAttempt;
  readonly onClick: () => void;
  readonly onRetry?: () => void | Promise<void>;
  readonly highlighted?: boolean;
}) {
  const { t } = useTranslation();
  const { result, module } = attempt;
  const isGraded = module.is_graded;
  const analysisInfo = getModuleAnalysisDisplayInfo(module);
  const canRetry =
    analysisInfo.state === "failed" &&
    analysisInfo.canRetry &&
    Boolean(onRetry);
  const canOpenReview = isModuleReviewAvailable(module);
  const isInteractive = canOpenReview || canRetry;

  return (
    <button
      type="button"
      disabled={!isInteractive}
      onClick={() => {
        if (canOpenReview) {
          onClick();
          return;
        }
        if (canRetry) {
          void onRetry?.();
        }
      }}
      className={cn(
        "flex w-full flex-col items-stretch justify-between gap-3 rounded-lg border border-border/60 bg-card p-3 text-left transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 sm:flex-row sm:items-center",
        isInteractive
          ? "cursor-pointer hover:border-border hover:bg-accent/40"
          : "cursor-not-allowed opacity-70",
        canRetry && "hover:border-destructive/30 hover:bg-destructive/5",
        highlighted &&
          "ring-2 ring-primary/50 ring-offset-2 animate-[pulse_1.5s_ease-in-out_3]",
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5 shrink-0" />
            {formatAttemptDate(result.date)}
          </span>
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="text-[10px]">
              {examTypeLabels[result.examType]}
            </Badge>
            <Badge variant="secondary" className="text-[10px]">
              {result.level}
            </Badge>
          </span>
        </div>
      </div>
      <div className="flex min-w-0 shrink-0 flex-wrap items-center justify-between gap-2 sm:justify-end sm:gap-3">
        {canOpenReview ? (
          <>
            <span
              className={cn(
                "text-lg font-bold tabular-nums tracking-tight",
                module.passed ? "text-primary" : "text-destructive",
              )}
            >
              {module.percentage}%
            </span>
            <Badge
              variant={module.passed ? "default" : "destructive"}
              className="text-[10px]"
            >
              {module.passed
                ? t("common.passed", { defaultValue: "Passed" })
                : t("common.failed", { defaultValue: "Failed" })}
            </Badge>
          </>
        ) : isGraded ? (
          <>
            <span
              className={cn(
                "text-lg font-bold tabular-nums tracking-tight",
                module.passed ? "text-primary" : "text-destructive",
              )}
            >
              {module.percentage}%
            </span>
            <span className="flex items-center gap-1 text-xs text-amber-600">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t("simulation.results.status.analysing", {
                defaultValue: "Analysing...",
              })}
            </span>
          </>
        ) : analysisInfo.state === "failed" ? (
          <span className="flex items-center gap-1 text-xs font-medium text-destructive">
            {canRetry ? (
              <RotateCcw className="h-3.5 w-3.5" />
            ) : (
              <AlertTriangle className="h-3.5 w-3.5" />
            )}
            {canRetry
              ? t("simulation.results.status.retry", { defaultValue: "Retry" })
              : t("simulation.results.status.failed", {
                  defaultValue: "Analysis failed",
                })}
          </span>
        ) : analysisInfo.state === "delayed" ? (
          <span className="flex items-center gap-1 text-xs text-amber-700">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t("simulation.results.status.delayed", {
              defaultValue: "Taking longer than expected",
            })}
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs text-amber-600">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t("simulation.results.status.analysing", {
              defaultValue: "Analysing...",
            })}
          </span>
        )}
      </div>
    </button>
  );
}

function ModuleGroup({
  moduleKey,
  attempts,
  onModuleClick,
  onModuleRetry,
  highlightedSessionId,
  highlightedModuleName,
  registerAttemptRef,
  foldedModules,
  onModuleFoldChange,
}: {
  readonly moduleKey: SimulationModuleKey;
  readonly attempts: ModuleAttempt[];
  readonly onModuleClick: (resultId: string, moduleName: string) => void;
  readonly onModuleRetry?: (
    resultId: string,
    moduleName: string,
  ) => void | Promise<void>;
  readonly highlightedSessionId: string | null;
  readonly highlightedModuleName: string | null;
  readonly registerAttemptRef: (
    key: string,
    element: HTMLDivElement | null,
  ) => void;
  readonly foldedModules: ReadonlySet<SimulationModuleKey>;
  readonly onModuleFoldChange: (
    moduleKey: SimulationModuleKey,
    isOpen: boolean,
  ) => void;
}) {
  const { t } = useTranslation();
  const Icon = SIMULATION_MODULE_ICONS[moduleKey];
  const color = SIMULATION_MODULE_COLORS[moduleKey];

  const gradedAttempts = attempts.filter((a) => a.module.is_graded);
  const averagePercentage = gradedAttempts.length
    ? Math.round(
        gradedAttempts.reduce((sum, a) => sum + a.module.percentage, 0) /
          gradedAttempts.length,
      )
    : null;

  const containsDeepLinkHighlight =
    moduleKey === highlightedModuleName &&
    attempts.some((attempt) => attempt.result.id === highlightedSessionId);

  const open = containsDeepLinkHighlight || !foldedModules.has(moduleKey);

  return (
    <Collapsible
      open={open}
      onOpenChange={(isOpen) => onModuleFoldChange(moduleKey, isOpen)}
      className="rounded-xl border border-border bg-muted/20 p-4"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex w-full items-center justify-between gap-2 rounded-md text-left transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 sm:gap-3",
            open && "mb-3 border-b border-border/60 pb-3",
          )}
        >
          <div className="flex items-center gap-2.5">
            <div
              className="shrink-0 rounded-md border border-border bg-background p-1.5"
              style={{ color }}
            >
              <Icon className="h-4 w-4" style={{ color: "inherit" }} />
            </div>
            <div>
              <h3 className="text-sm font-semibold">
                {t(`moduleSelection.modules.${moduleKey}.label`, {
                  defaultValue: SIMULATION_MODULE_SHORT_LABELS[moduleKey],
                })}
              </h3>
              <p className="text-xs text-muted-foreground">
                {t("simulation.results.history.attemptsCount", {
                  count: attempts.length,
                  defaultValue: "{{count}} attempts",
                })}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {averagePercentage !== null && (
              <div className="flex w-20 flex-col items-end gap-1 sm:w-28">
                <span className="text-xs text-muted-foreground">
                  {t("simulation.results.history.average", {
                    defaultValue: "Average",
                  })}
                </span>
                <span
                  className="text-sm font-bold tabular-nums"
                  style={{ color }}
                >
                  {averagePercentage}%
                </span>
                <Progress
                  value={averagePercentage}
                  className="h-1.5 w-full"
                  style={{ "--progress-background": color } as CSSProperties}
                />
              </div>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
                open && "rotate-180",
              )}
            />
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-2">
          {attempts.map((attempt) => {
            const refKey = attemptRefKey(
              attempt.result.id,
              attempt.module.module,
            );
            const highlighted =
              attempt.result.id === highlightedSessionId &&
              attempt.module.module === highlightedModuleName;

            return (
              <div
                key={refKey}
                ref={(element) => registerAttemptRef(refKey, element)}
              >
                <ModuleAttemptRow
                  attempt={attempt}
                  highlighted={highlighted}
                  onClick={() =>
                    onModuleClick(attempt.result.id, attempt.module.module)
                  }
                  onRetry={() =>
                    onModuleRetry?.(attempt.result.id, attempt.module.module)
                  }
                />
              </div>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function SimulationHistorySection({
  results,
  highlightedSessionId,
  highlightedModuleName,
  onModuleClick,
  onModuleRetry,
  view,
  statusFilter,
  onViewChange,
  onStatusFilterChange,
  foldedModules,
  onFoldedModulesChange,
}: SimulationHistorySectionProps) {
  const { t } = useTranslation();
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  /** Avoid repeat scrollIntoView when filters change or results poll (same deep-link). */
  const scrolledToTargetRef = useRef<string | null>(null);

  const registerAttemptRef = (key: string, element: HTMLDivElement | null) => {
    if (element) {
      cardRefs.current.set(key, element);
    } else {
      cardRefs.current.delete(key);
    }
  };

  const handleModuleFoldChange = useCallback(
    (moduleKey: SimulationModuleKey, isOpen: boolean) => {
      const next = new Set(foldedModules);
      if (isOpen) {
        next.delete(moduleKey);
      } else {
        next.add(moduleKey);
      }
      onFoldedModulesChange(next);
    },
    [foldedModules, onFoldedModulesChange],
  );

  const isFullyGraded = (result: SimulationResult) =>
    result.modules.length > 0 && result.modules.every((m) => m.is_graded);

  const simulationResults = useMemo(() => {
    if (statusFilter === "all") return results;
    return results.filter(
      (result) =>
        isFullyGraded(result) && result.passed === (statusFilter === "passed"),
    );
  }, [results, statusFilter]);

  const moduleGroups = useMemo(() => {
    return MODULE_ORDER.map((moduleKey) => {
      const attempts: ModuleAttempt[] = [];
      results.forEach((result) => {
        const module = result.modules.find((m) => m.module === moduleKey);
        if (!module) return;
        if (statusFilter !== "all") {
          if (!module.is_graded) return;
          if (module.passed !== (statusFilter === "passed")) return;
        }
        attempts.push({ result, module });
      });
      return { moduleKey, attempts };
    }).filter((group) => group.attempts.length > 0);
  }, [results, statusFilter]);

  // Deep-link scroll: By Simulation (session card) or By Module (attempt row).
  useEffect(() => {
    if (!highlightedSessionId) {
      scrolledToTargetRef.current = null;
      return;
    }

    if (view === "simulation") {
      if (scrolledToTargetRef.current === highlightedSessionId) {
        return;
      }

      const targetCard = cardRefs.current.get(highlightedSessionId);
      if (!targetCard) {
        return;
      }

      scrolledToTargetRef.current = highlightedSessionId;
      targetCard.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (view !== "module" || !highlightedModuleName) {
      return;
    }

    const scrollKey = attemptRefKey(
      highlightedSessionId,
      highlightedModuleName,
    );
    if (scrolledToTargetRef.current === scrollKey) {
      return;
    }

    const targetRow = cardRefs.current.get(scrollKey);
    if (!targetRow) {
      return;
    }

    scrolledToTargetRef.current = scrollKey;
    targetRow.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [
    highlightedSessionId,
    highlightedModuleName,
    view,
    simulationResults,
    moduleGroups,
  ]);

  const visibleCount =
    view === "simulation"
      ? simulationResults.length
      : moduleGroups.reduce((sum, group) => sum + group.attempts.length, 0);

  const renderBody = () => {
    if (visibleCount === 0) {
      return (
        <div className="py-12 text-center text-muted-foreground">
          <Award className="mx-auto mb-4 h-12 w-12 opacity-50" />
          <p>{t("simulation.results.noSimulations.title")}</p>
          <p className="text-sm">
            {t("simulation.results.noSimulations.description")}
          </p>
        </div>
      );
    }

    if (view === "module") {
      return (
        <div className="space-y-5">
          {moduleGroups.map((group) => (
            <ModuleGroup
              key={group.moduleKey}
              moduleKey={group.moduleKey}
              attempts={group.attempts}
              onModuleClick={onModuleClick}
              onModuleRetry={onModuleRetry}
              highlightedSessionId={highlightedSessionId}
              highlightedModuleName={highlightedModuleName}
              registerAttemptRef={registerAttemptRef}
              foldedModules={foldedModules}
              onModuleFoldChange={handleModuleFoldChange}
            />
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {simulationResults.map((result) => (
          <div
            key={result.id}
            ref={(element) => {
              if (element) {
                cardRefs.current.set(result.id, element);
              } else {
                cardRefs.current.delete(result.id);
              }
            }}
          >
            <SimulationResultCard
              result={result}
              highlighted={result.id === highlightedSessionId}
              onModuleClick={(moduleName) =>
                onModuleClick(result.id, moduleName)
              }
              onModuleRetry={(moduleName) =>
                onModuleRetry?.(result.id, moduleName)
              }
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="gap-4 p-4 sm:p-6">
        <div className="flex flex-col gap-1">
          <CardTitle>
            {t("simulation.results.history.title", {
              defaultValue: "Simulation History",
            })}
          </CardTitle>
          <CardDescription>
            {visibleCount}{" "}
            {visibleCount === 1
              ? t("simulation.results.history.simulation")
              : t("simulation.results.history.simulations")}{" "}
            {t("common.found")}
          </CardDescription>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Tabs
            value={view}
            onValueChange={(value) => onViewChange(value as HistoryView)}
          >
            <TabsList className="grid w-full grid-cols-2 sm:w-auto">
              <TabsTrigger value="simulation" className="gap-1.5 px-3">
                <List className="h-3.5 w-3.5" />
                {t("simulation.results.history.viewBy.simulation", {
                  defaultValue: "By Simulation",
                })}
              </TabsTrigger>
              <TabsTrigger value="module" className="gap-1.5 px-3">
                <Layers className="h-3.5 w-3.5" />
                {t("simulation.results.history.viewBy.module", {
                  defaultValue: "By Module",
                })}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex min-w-0 items-center gap-2">
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              {t("simulation.results.history.statusFilter.label", {
                defaultValue: "Status",
              })}
              :
            </span>
            <Select
              value={statusFilter}
              onValueChange={(value) =>
                onStatusFilterChange(value as StatusFilter)
              }
            >
              <SelectTrigger className="min-w-0 flex-1 sm:w-[150px] sm:flex-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("simulation.results.history.statusFilter.all", {
                    defaultValue: "All",
                  })}
                </SelectItem>
                <SelectItem value="passed">
                  {t("common.passed", { defaultValue: "Passed" })}
                </SelectItem>
                <SelectItem value="failed">
                  {t("common.failed", { defaultValue: "Failed" })}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
        {renderBody()}
      </CardContent>
    </Card>
  );
}
